/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  TextField,
  Typography,
  Stack,
  Link,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  useTheme,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { SelectChangeEvent } from '@mui/material/Select';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { MuiTelInput } from 'mui-tel-input';
import {
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  EmailAuthProvider,
  linkWithCredential,
  createUserWithEmailAndPassword,
  ConfirmationResult,
  PhoneAuthCredential,
} from 'firebase/auth';
import { useRegister, useEmailCheck } from '@/api/auth';
import { registerSchema, RegisterFormData } from '@/validations/auth';
import { initializeFirebase } from '@/config/firebase';


type RegistrationOption = {
  value: string;
  label: string;
  labelZh?: string;
  labelCn?: string;
};

type RegistrationOptions = {
  genders: RegistrationOption[];
  months: RegistrationOption[];
};

export default function RegisterPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<RegisterFormData>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    resolver: yupResolver(registerSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: undefined,
      address: undefined,
      city: undefined,
      state: undefined,
      country: undefined,
      zipCode: undefined,
      birthMonth: '',
      birthDay: '',
      birthYear: undefined,
      gender: undefined,
      referrer: undefined,
      marketingConsent: false,
      termsAccepted: false,
      privacyPolicyAccepted: false,
      dataCollectionConsent: false,
      emailNotifications: false,
      mobileAppPush: false,
    },
  });

  const [verifiedPhone, setVerifiedPhone] = useState<string | undefined>(() => {
    try {
      const phone = sessionStorage.getItem('phoneLogin_verifiedPhone');
      if (phone) {
        sessionStorage.removeItem('phoneLogin_verifiedPhone');
        return phone;
      }
    } catch {}
    return undefined;
  });

  const { register: registerUser, isLoading, error } = useRegister();
  const { checkEmail, isLoading: isCheckingEmail } = useEmailCheck();
  
  // Static registration options with translations
  const staticOptions: RegistrationOptions = {
    genders: [
      {
        value: "male",
        label: "Male",
        labelZh: "男",
        labelCn: "男"
      },
      {
        value: "female",
        label: "Female",
        labelZh: "女",
        labelCn: "女"
      }
    ],
    months: [
      { value: "01", label: "January", labelZh: "一月", labelCn: "一月" },
      { value: "02", label: "February", labelZh: "二月", labelCn: "二月" },
      { value: "03", label: "March", labelZh: "三月", labelCn: "三月" },
      { value: "04", label: "April", labelZh: "四月", labelCn: "四月" },
      { value: "05", label: "May", labelZh: "五月", labelCn: "五月" },
      { value: "06", label: "June", labelZh: "六月", labelCn: "六月" },
      { value: "07", label: "July", labelZh: "七月", labelCn: "七月" },
      { value: "08", label: "August", labelZh: "八月", labelCn: "八月" },
      { value: "09", label: "September", labelZh: "九月", labelCn: "九月" },
      { value: "10", label: "October", labelZh: "十月", labelCn: "十月" },
      { value: "11", label: "November", labelZh: "十一月", labelCn: "十一月" },
      { value: "12", label: "December", labelZh: "十二月", labelCn: "十二月" }
    ]
  };
  
  const options = staticOptions;
  const navigate = useNavigate();
  
  // Function to get translated label based on current language
  const getTranslatedLabel = (option: RegistrationOption): string => {
    const currentLanguage = i18n.language;
    switch (currentLanguage) {
      case 'zh':
        return option.labelZh || option.label;
      case 'cn':
        return option.labelCn || option.label;
      default:
        return option.label;
    }
  };

  // Phone SMS verification state
  const [smsSent, setSmsSent] = useState(false);
  const [smsVerified, setSmsVerified] = useState(!!verifiedPhone);
  const [sendingSms, setSendingSms] = useState(false);
  const [smsError, setSmsError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [smsCode, setSmsCode] = useState('');
  const confirmationResultRef = useRef<ConfirmationResult | null>(null);
  const phoneCredentialRef = useRef<PhoneAuthCredential | null>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  // Email validation state
  const [emailExists, setEmailExists] = useState<boolean | null>(null);
  const [emailCheckMessage, setEmailCheckMessage] = useState<string>('');
  
  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Ref to store the checkEmail function to avoid dependency issues
  const checkEmailRef = useRef(checkEmail);
  checkEmailRef.current = checkEmail;

  // Pre-fill phone from phone-login flow
  useEffect(() => {
    if (verifiedPhone) {
      setValue('phone', verifiedPhone);
    }
  }, [verifiedPhone, setValue]);

  // Countdown timer effect
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [countdown]);

  // Check email existence function
  const handleEmailCheck = useCallback(async (email: string) => {
    if (!email || !email.includes('@')) {
      setEmailExists(null);
      setEmailCheckMessage('');
      return;
    }

    try {
      const response = await checkEmailRef.current(email);
      setEmailExists(response.exists);
      setEmailCheckMessage(response.message || '');
    } catch (error: unknown) {
      setEmailExists(null);
      if (error && typeof error === 'object' && 'response' in error) {
        const apiError = error as { response?: { data?: { message?: string } } };
        setEmailCheckMessage(apiError.response?.data?.message || 'Failed to check email');
      } else {
        setEmailCheckMessage('Failed to check email');
      }
    }
  }, []); // Empty dependency array since we use ref

  // Email check effect with debouncing
  const emailValue = watch('email');
  useEffect(() => {
    if (!emailValue || !emailValue.includes('@')) {
      setEmailExists(null);
      setEmailCheckMessage('');
      return;
    }

    const timeoutId = setTimeout(() => {
      handleEmailCheck(emailValue);
    }, 500); // 500ms debounce

    return () => clearTimeout(timeoutId);
  }, [emailValue, handleEmailCheck]); // Now safe to include handleEmailCheck

  // Initialise the invisible reCAPTCHA verifier once when the component mounts.
  // It is attached to the "send-sms-btn" div so Firebase can render the widget there.
  useEffect(() => {
    let verifier: RecaptchaVerifier | null = null;
    initializeFirebase().then(({ auth }) => {
      verifier = new RecaptchaVerifier(auth, 'send-sms-btn', { size: 'invisible' });
      recaptchaVerifierRef.current = verifier;
    }).catch(() => {
      // Firebase may not be ready yet; reCAPTCHA will be created lazily on first send.
    });
    return () => {
      verifier?.clear();
    };
  }, []);

  const handleSendSMS = async () => {
    const phoneValue = watch('phone');
    if (!phoneValue) {
      setSmsError(t('auth.errors.phoneRequired'));
      return;
    }

    setSendingSms(true);
    setSmsError(null);
    setSmsVerified(false);
    setSmsCode('');
    phoneCredentialRef.current = null;

    try {
      const { auth } = await initializeFirebase();

      // Lazily create verifier if the mount-time init failed
      if (!recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current = new RecaptchaVerifier(auth, 'send-sms-btn', { size: 'invisible' });
      }

      const result = await signInWithPhoneNumber(auth, phoneValue, recaptchaVerifierRef.current);
      confirmationResultRef.current = result;
      setSmsSent(true);
      setCountdown(90);
    } catch (error: unknown) {
      // Recycle the verifier so the next attempt works
      recaptchaVerifierRef.current?.clear();
      recaptchaVerifierRef.current = null;

      const firebaseError = error as { code?: string; message?: string };
      if (firebaseError.code === 'auth/invalid-phone-number') {
        setSmsError(t('auth.errors.invalidPhoneNumber'));
      } else if (firebaseError.code === 'auth/too-many-requests') {
        setSmsError(t('auth.errors.tooManyRequests'));
      } else if (firebaseError.code === 'auth/credential-already-in-use') {
        setSmsError(t('auth.errors.phoneAlreadyRegistered'));
      } else {
        setSmsError(firebaseError.message || t('auth.errors.failedToSendCode'));
      }
    } finally {
      setSendingSms(false);
    }
  };

  const handleSmsCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 6);
    setSmsCode(digits);
    setSmsError(null);

    if (digits.length === 6 && confirmationResultRef.current) {
      // Store the credential optimistically; final verification happens on link during submit
      const credential = PhoneAuthProvider.credential(
        confirmationResultRef.current.verificationId,
        digits
      );
      phoneCredentialRef.current = credential;
      setSmsVerified(true);
    } else {
      phoneCredentialRef.current = null;
      setSmsVerified(false);
    }
  };

  const watchedTerms = watch([
    'termsAccepted',
    'privacyPolicyAccepted',
    'dataCollectionConsent',
    'marketingConsent',
    'emailNotifications',
    'mobileAppPush',
  ]);

  const handleTermsChange =
    (name: keyof RegisterFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      setValue(name, isChecked);

      // Hierarchical checkbox logic
      if (name === 'marketingConsent') {
        // When marketing consent is checked/unchecked, check/uncheck email and mobile app push
        setValue('emailNotifications', isChecked);
        setValue('mobileAppPush', isChecked);
      } else if (name === 'emailNotifications' || name === 'mobileAppPush') {
        // When email or mobile app push is checked, check marketing consent
        if (isChecked) {
          setValue('marketingConsent', true);
        } else {
          // If both email and mobile app push are unchecked, uncheck marketing consent
          const emailChecked = name === 'emailNotifications' ? false : watch('emailNotifications');
          const mobileChecked = name === 'mobileAppPush' ? false : watch('mobileAppPush');
          if (!emailChecked && !mobileChecked) {
            setValue('marketingConsent', false);
          }
        }
      }
    };

  const handleAgreeAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    // Set all 6 checkboxes to the same value
    setValue('termsAccepted', isChecked);
    setValue('privacyPolicyAccepted', isChecked);
    setValue('dataCollectionConsent', isChecked);
    setValue('marketingConsent', isChecked);
    setValue('emailNotifications', isChecked);
    setValue('mobileAppPush', isChecked);
  };

  const handleGenderChange = (event: SelectChangeEvent) => {
    setValue('gender', event.target.value as string);
  };

  const handleMonthChange = (event: SelectChangeEvent) => {
    const monthValue = event.target.value;
    setValue('birthMonth', monthValue || '');
  };

  const onSubmit = handleSubmit(async data => {
    if (!smsVerified) {
      setSmsError(t('auth.errors.phoneVerificationRequired'));
      return;
    }

    try {
      const { auth } = await initializeFirebase();
      let firebaseUser;

      if (verifiedPhone) {
        // Coming from phone login — auth.currentUser is already the phone-verified Firebase user.
        // Link the email/password credential to it instead of creating a separate account.
        const currentUser = auth.currentUser;
        if (!currentUser) {
          // Firebase session gone — silently fall back to normal phone verification
          setVerifiedPhone(undefined);
          setSmsVerified(false);
          return;
        }

        const emailCredential = EmailAuthProvider.credential(data.email, data.password);
        try {
          const linked = await linkWithCredential(currentUser, emailCredential);
          firebaseUser = linked.user;
        } catch (linkError: unknown) {
          const err = linkError as { code?: string };
          if (err.code === 'auth/email-already-in-use') {
            setSmsError(t('auth.errors.emailAlreadyLinked'));
            return;
          } else if (err.code === 'auth/provider-already-linked') {
            firebaseUser = currentUser;
          } else {
            throw linkError;
          }
        }
      } else {
        // Normal path — create Firebase email/password user then link phone credential.
        if (!phoneCredentialRef.current) {
          setSmsError(t('auth.errors.phoneVerificationRequired'));
          return;
        }

        try {
          const userCred = await createUserWithEmailAndPassword(auth, data.email, data.password);
          firebaseUser = userCred.user;
        } catch (firebaseError: unknown) {
          const fbErr = firebaseError as { code?: string };
          if (fbErr.code === 'auth/email-already-in-use') {
            firebaseUser = auth.currentUser;
            if (!firebaseUser) {
              throw new Error('Account already exists. Please sign in to complete registration.');
            }
          } else {
            throw firebaseError;
          }
        }

        if (!firebaseUser) throw new Error('Failed to create account. Please try again.');

        try {
          await linkWithCredential(firebaseUser, phoneCredentialRef.current);
        } catch (linkError: unknown) {
          const linkErr = linkError as { code?: string };
          if (linkErr.code === 'auth/credential-already-in-use') {
            setSmsError(t('auth.errors.phoneAlreadyRegistered'));
            return;
          } else if (linkErr.code === 'auth/invalid-verification-code') {
            setSmsError(t('auth.errors.invalidCode'));
            setSmsVerified(false);
            phoneCredentialRef.current = null;
            setSmsCode('');
            return;
          } else if (linkErr.code !== 'auth/provider-already-linked') {
            throw linkError;
          }
        }
      }

      // Force-refresh token so phone_number claim is included — backend requires it
      await firebaseUser.getIdToken(true);

      const apiData = {
        ...data,
        name: `${data.firstName} ${data.lastName}`,
        birthMonth: data.birthMonth ? parseInt(data.birthMonth, 10) : undefined,
        birthDay: data.birthDay ? parseInt(data.birthDay, 10) : undefined,
      };

      await registerUser(apiData as unknown as RegisterFormData & {
        name: string;
        birthMonth?: number;
        birthDay?: number;
      });

      navigate('/sign-in');
    } catch (error: unknown) {
      console.error('Registration failed:', error);
    }
  });

  return (
    <Container
      maxWidth="xs"
      sx={{
        py: 4,
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ width: '100%' }}>
        <Typography variant="h5" fontWeight={700} align="center" gutterBottom>
          {t('auth.join')}
        </Typography>
        <Typography
          variant="subtitle1"
          align="center"
          fontWeight={700}
          sx={{ mb: 0.5 }}
        >
          {t('benefits.dontMissOut')}
        </Typography>
        <Typography variant="body2" align="center" sx={{ mb: 2 }}>
          {t('benefits.hatsuhikenBenefits')}
        </Typography>
        
        {/* API Error Display */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error.message}
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <img
              src="/icons/ic_b_day_off.svg"
              alt="B-DAY"
              width={40}
              height={40}
            />
            <Typography
              variant="caption"
              sx={{ color: theme.palette.custom.brown, fontWeight: 500, mt: 1 }}
            >
              {t('benefits.bdayOff')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <img
              src="/icons/ic_app_point.svg"
              alt="Points"
              width={40}
              height={40}
            />
            <Typography
              variant="caption"
              sx={{ color: theme.palette.custom.brown, fontWeight: 500, mt: 1 }}
            >
              {t('benefits.appInstallPoints')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <img
              src="/icons/ic_coupon.svg"
              alt="Coupon"
              width={40}
              height={40}
            />
            <Typography
              variant="caption"
              sx={{ color: '#b8957b', fontWeight: 500, mt: 1 }}
            >
              {t('benefits.hk10OffCoupon')}
            </Typography>
          </Box>
        </Box>
        <Typography align="center" sx={{ mb: 2 }}>
          {t('brand.enjoyExclusiveServices')}
          <br />
          <b>{t('brand.fromHatsuhiken')}</b>
        </Typography>
        <form onSubmit={onSubmit}>
          <Stack spacing={'12px'}>
            <Typography fontWeight={700} fontSize="16px">
              {t('auth.firstName')} <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <TextField
              {...register('firstName')}
              placeholder={t('auth.firstName')}
              required
              fullWidth
              size="small"
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />
            <TextField
              {...register('lastName')}
              placeholder={t('auth.lastName')}
              required
              fullWidth
              size="small"
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
            />
            <Typography fontWeight={700} fontSize="16px">
              {t('auth.emailId')} <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <TextField
              {...register('email')}
              placeholder={t('auth.emailHelper')}
              required
              fullWidth
              size="small"
              error={!!errors.email || emailExists === true}
              helperText={
                errors.email?.message || 
                (emailExists === false ? t('auth.errors.emailAvailable') : '') ||
                (isCheckingEmail ? t('auth.errors.checkingEmail') : '')
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: emailExists === true ? '#ffebee' : 'transparent',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: emailExists === true ? '#d32f2f' : undefined,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: emailExists === true ? '#d32f2f' : undefined,
                  },
                },
              }}
            />
            {emailCheckMessage && (
              <Alert 
                severity={emailExists === true ? 'error' : emailExists === false ? 'success' : 'info'}
                sx={{ mt: 1 }}
              >
                {emailCheckMessage}
              </Alert>
            )}
            
            {/* Phone Number + SMS Verification Section */}
            <Typography fontWeight={700} fontSize="16px">
              {t('auth.phoneNumber')} <span style={{ color: 'red' }}>*</span>
            </Typography>
            <Controller
              name="phone"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <MuiTelInput
                  {...field}
                  value={field.value || ''}
                  onChange={(value) => {
                    if (verifiedPhone) return;
                    field.onChange(value);
                    // Reset SMS state when phone number changes
                    if (smsSent) {
                      setSmsSent(false);
                      setSmsVerified(false);
                      setSmsCode('');
                      phoneCredentialRef.current = null;
                      setSmsError(null);
                    }
                  }}
                  fullWidth
                  size="small"
                  variant="outlined"
                  disabled={!!verifiedPhone}
                  sx={{
                    '& .MuiTelInput-flagButton': {
                      borderColor: '#E1DACA',
                    },
                    '& .MuiTelInput-flagButton:hover': {
                      borderColor: '#E1DACA',
                    },
                    '& .MuiTelInput-flagButton.Mui-focused': {
                      borderColor: '#E1DACA',
                    },
                  }}
                  error={!!errors.phone}
                  helperText={errors.phone?.message}
                  defaultCountry="HK"
                  forceCallingCode={true}
                  disableFormatting={false}
                  placeholder={t('auth.phoneNumberPlaceholder')}
                />
              )}
            />

            {/* Show verified badge when phone came from phone-login flow */}
            {verifiedPhone && (
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#4caf50', fontSize: '14px' }}>
                <Box sx={{
                  width: 16, height: 16, borderRadius: '50%', bgcolor: '#4caf50',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 0.5,
                }}>
                  <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</Typography>
                </Box>
                {t('auth.phoneVerified')}
              </Box>
            )}

            {/* SMS verification UI — hidden when phone is pre-verified from phone-login */}
            {!verifiedPhone && (
              <>
                {/* Invisible reCAPTCHA anchor — Firebase attaches the widget here */}
                <div id="send-sms-btn" />

                {/* Send SMS button — shown when phone is not yet verified */}
                {!smsVerified && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSendSMS}
                    disabled={sendingSms || countdown > 0 || !watch('phone') || !!errors.phone}
                    sx={{
                      height: '36px',
                      fontSize: '12px',
                      bgcolor: theme.palette.custom.brown,
                      color: 'white',
                      fontWeight: 700,
                      '&:hover': { bgcolor: '#a98c74' },
                      '&:disabled': { bgcolor: '#ccc', color: '#999' },
                    }}
                  >
                    {sendingSms ? (
                      <CircularProgress size={16} sx={{ color: 'white' }} />
                    ) : countdown > 0 ? (
                      `${t('auth.resendCode')} (${countdown}s)`
                    ) : smsSent ? (
                      t('auth.resendCode')
                    ) : (
                      t('auth.sendCode')
                    )}
                  </Button>
                )}
              </>
            )}

            {/* SMS code input — shown after SMS is sent */}
            {smsSent && !verifiedPhone && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography fontWeight={700} fontSize="16px">
                  {t('auth.verificationCode')} <span style={{ color: '#d32f2f' }}>*</span>
                </Typography>
                <TextField
                  value={smsCode}
                  placeholder={t('auth.verificationCodePlaceholder')}
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: smsVerified && (
                      <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#4caf50',
                        mr: 1,
                      }}>
                        <Box sx={{
                          width: 16,
                          height: 16,
                          borderRadius: '50%',
                          bgcolor: '#4caf50',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 0.5,
                        }}>
                          <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</Typography>
                        </Box>
                      </Box>
                    ),
                  }}
                  inputProps={{
                    maxLength: 6,
                    style: { textAlign: 'center', fontSize: '14px', fontWeight: 'bold' },
                  }}
                  onChange={(e) => handleSmsCodeChange(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      ...(smsVerified && {
                        '& fieldset': { borderColor: '#4caf50' },
                        '&:hover fieldset': { borderColor: '#4caf50' },
                        '&.Mui-focused fieldset': { borderColor: '#4caf50' },
                      }),
                      ...(smsError && !smsVerified && {
                        '& fieldset': { borderColor: '#d32f2f' },
                        '&:hover fieldset': { borderColor: '#d32f2f' },
                        '&.Mui-focused fieldset': { borderColor: '#d32f2f' },
                      }),
                    },
                  }}
                />

                {smsVerified && (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: '#4caf50', fontSize: '14px' }}>
                    <Box sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: '#4caf50',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 0.5,
                    }}>
                      <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✓</Typography>
                    </Box>
                    {t('auth.verificationCodeValid')}
                  </Box>
                )}

                {smsError && !smsVerified && (
                  <Box sx={{ display: 'flex', alignItems: 'center', color: '#d32f2f', fontSize: '14px' }}>
                    <Box sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      bgcolor: '#d32f2f',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 0.5,
                    }}>
                      <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✗</Typography>
                    </Box>
                    {smsError}
                  </Box>
                )}
              </Box>
            )}

            {/* Top-level SMS send error (shown before SMS is sent) */}
            {smsError && !smsSent && !verifiedPhone && (
              <Box sx={{ display: 'flex', alignItems: 'center', color: '#d32f2f', fontSize: '14px' }}>
                <Box sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  bgcolor: '#d32f2f',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mr: 0.5,
                }}>
                  <Typography sx={{ color: 'white', fontSize: '12px', fontWeight: 'bold' }}>✗</Typography>
                </Box>
                {smsError}
              </Box>
            )}
            <Typography fontWeight={700} fontSize="16px">
              {t('auth.password')} <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <TextField
              {...register('password')}
              placeholder={t('auth.passwordHelper')}
              required
              fullWidth
              size="small"
              type={showPassword ? 'text' : 'password'}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography fontWeight={700} fontSize="16px">
              {t('auth.confirmPassword')}{' '}
              <span style={{ color: '#d32f2f' }}>*</span>
            </Typography>
            <TextField
              {...register('confirmPassword')}
              placeholder={t('auth.confirmPassword')}
              required
              fullWidth
              size="small"
              type={showConfirmPassword ? 'text' : 'password'}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Typography fontWeight={700} fontSize="16px">
              {t('register.birthday')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControl size="small" sx={{ flex: 1 }}>
                <Controller
                  name="birthMonth"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value || ''}
                      onChange={handleMonthChange}
                      displayEmpty
                      error={!!errors.birthMonth}
                    >
                      <MenuItem value="" disabled>
                        {t('register.selectMonth')}
                      </MenuItem>
                      {options?.months?.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {getTranslatedLabel(option)}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.birthMonth && (
                  <FormHelperText error>
                    {errors.birthMonth.message}
                  </FormHelperText>
                )}
              </FormControl>
              <TextField
                {...register('birthDay')}
                placeholder={t('register.selectDay')}
                size="small"
                sx={{ flex: 1 }}
                type="text"
                inputProps={{ maxLength: 2 }}
                error={!!errors.birthDay}
                helperText={errors.birthDay?.message}
                onChange={(e) => {
                  const value = e.target.value;
                  // Only allow numbers and limit to 2 digits
                  if (/^\d{0,2}$/.test(value)) {
                    setValue('birthDay', value);
                  }
                }}
              />

            </Box>
            <Typography variant="caption" sx={{ mt: 0, mb: 0 }}>
              {t('register.birthdayCoupon')}
            </Typography>
            <Typography fontWeight={700} fontSize="16px">
              {t('register.gender')}
            </Typography>
            <FormControl fullWidth size="small">
              <Controller
                name="gender"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <Select
                    {...field}
                    value={field.value || ''}
                    onChange={handleGenderChange}
                    displayEmpty
                    error={!!errors.gender}
                  >
                    <MenuItem value="" disabled>
                      {t('register.selectGender')}
                    </MenuItem>
                      {options?.genders?.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {getTranslatedLabel(option)}
                        </MenuItem>
                      ))}
                  </Select>
                )}
              />
              {errors.gender && (
                <FormHelperText error>{errors.gender.message}</FormHelperText>
              )}
            </FormControl>
            <Typography fontWeight={700} fontSize="16px">
              {t('register.referrer')}
            </Typography>
            <TextField
              {...register('referrer')}
              placeholder={t('register.referrerHelper')}
              fullWidth
              size="small"
              error={!!errors.referrer}
              helperText={errors.referrer?.message}
            />
          </Stack>
          <Box sx={{ mt: 3 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={
                    watchedTerms[0] &&
                    watchedTerms[1] &&
                    watchedTerms[2] &&
                    watchedTerms[3] &&
                    watchedTerms[4] &&
                    watchedTerms[5]
                  }
                  onChange={handleAgreeAllChange}
                  size="small"
                  sx={{
                    color: '#3D3C3C',
                    '&.Mui-checked': { color: '#3D3C3C' },
                  }}
                />
              }
              label={
                <Typography variant="body2" fontWeight={700}>
                  {t('register.agreeAll')}
                </Typography>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={watchedTerms[0]}
                  onChange={handleTermsChange('termsAccepted')}
                  size="small"
                  sx={{
                    color: '#3D3C3C',
                    '&.Mui-checked': { color: '#3D3C3C' },
                  }}
                />
              }
              label={
                <span>
                  <Typography variant="body2" component="span">
                    <Link
                      href="/page/terms-and-policy"
                      target="_blank"
                      underline="always"
                      sx={{ color: '#3D3C3C', textDecoration: 'underline' }}
                    >
                      {t('register.termsOfUse')}
                    </Link>{' '}
                    {t('register.termsRequired')}
                  </Typography>
                </span>
              }
            />
            {errors.termsAccepted && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: 'block', ml: 4 }}
              >
                {errors.termsAccepted.message}
              </Typography>
            )}
            {errors.privacyPolicyAccepted && (
              <Typography
                variant="caption"
                color="error"
                sx={{ display: 'block', ml: 4 }}
              >
                {errors.privacyPolicyAccepted.message}
              </Typography>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={watchedTerms[2]}
                  onChange={handleTermsChange('dataCollectionConsent')}
                  size="small"
                  sx={{
                    color: '#3D3C3C',
                    '&.Mui-checked': { color: '#3D3C3C' },
                  }}
                />
              }
              label={
                <span>
                  <Typography variant="body2" component="span">
                    {t('register.personalOptional')}
                  </Typography>
                  <br />
                  <Link
                    href="/page/collection-information-policy"
                    target="_blank"
                    underline="always"
                    variant="caption"
                    sx={{
                      color: theme.palette.custom.brown,
                      textDecoration: 'underline',
                      textDecorationColor: theme.palette.custom.brown,
                      fontSize: '14px',
                    }}
                  >
                    {t('register.readMore')}
                  </Link>
                </span>
              }
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={watchedTerms[3]}
                  onChange={handleTermsChange('marketingConsent')}
                  size="small"
                  sx={{
                    color: '#3D3C3C',
                    '&.Mui-checked': { color: '#3D3C3C' },
                  }}
                />
              }
              label={
                <span>
                  <Typography variant="body2" component="span">
                    {t('register.marketing')}
                  </Typography>
                  <br />
                  <Link
                    href="/page/marketing-policy"
                    target="_blank"
                    underline="always"
                    variant="caption"
                    sx={{
                      color: theme.palette.custom.brown,
                      textDecoration: 'underline',
                      textDecorationColor: theme.palette.custom.brown,
                      fontSize: '14px',
                    }}
                  >
                    {t('register.readMore')}
                  </Link>
                </span>
              }
            />
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={watchedTerms[4]}
                    onChange={handleTermsChange('emailNotifications')}
                    size="small"
                    sx={{
                      color: '#3D3C3C',
                      '&.Mui-checked': { color: '#3D3C3C' },
                    }}
                  />
                }
                label={
                  <Typography variant="body2">{t('register.email')}</Typography>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={watchedTerms[5]}
                    onChange={handleTermsChange('mobileAppPush')}
                    size="small"
                    sx={{
                      color: '#3D3C3C',
                      '&.Mui-checked': { color: '#3D3C3C' },
                    }}
                  />
                }
                label={
                  <Typography variant="body2">
                    {t('register.mobileAppPush')}
                  </Typography>
                }
              />
            </Stack>
          </Box>
          {error && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mt: 2, textAlign: 'center' }}
            >
              {error.message}
            </Typography>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={isLoading || !smsVerified || emailExists === true}
            sx={{
              bgcolor: theme.palette.custom.brown,
              color: 'white',
              fontWeight: 700,
              mt: 3,
              '&:hover': { bgcolor: '#a98c74' },
            }}
          >
            {isLoading ? t('common.loading') : t('benefits.joinNow')}
          </Button>
        </form>
      </Box>
    </Container>
  );
}
